import { useEffect, useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { api } from "@/lib/api";
import { Plus, Minus, ArrowCounterClockwise, ArrowRight } from "@phosphor-icons/react";
import StateDetailPanel from "./StateDetailPanel";

// Public topojson sources (fetched once, cached by browser)
const US_STATES_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const CANADA_URL = "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson";

// Format numbers exactly as user requested: 120+, 600+, 4200+, 6000+
const formatCount = (n) => {
    if (!n) return "0";
    return `${n}+`;
};

const colorForCount = (n) => {
    if (!n) return "rgba(30,41,59,0.7)";
    if (n >= 6000) return "#06b6d4";   // cyan-500 (Florida)
    if (n >= 3000) return "#0891b2";   // cyan-600 (IL, TX, GA, MD)
    if (n >= 2000) return "#0e7490";   // cyan-700 (OH)
    if (n >= 1000) return "#155e75";   // cyan-800 (secondary states)
    if (n >= 500) return "#164e63";    // cyan-900 (600+)
    if (n >= 200) return "#0c3a4e";    // (240, 330, 420)
    return "#0a2e3d";                  // (120)
};

const DEFAULT_POSITION = { coordinates: [-96, 54], zoom: 1 };

// Popup visual tier configuration — avoids nested ternaries in JSX.
function popupTierStyle(count) {
    if (count >= 3000) {
        return {
            borderColor: "#06b6d4",
            boxShadow: "0 12px 48px rgba(6,182,212,0.5), 0 0 0 1px rgba(6,182,212,0.3)",
        };
    }
    if (count >= 500) {
        return { borderColor: "#0891b2", boxShadow: "0 12px 40px rgba(0,0,0,0.6)" };
    }
    return { borderColor: "rgba(148,163,184,0.4)", boxShadow: "0 12px 40px rgba(0,0,0,0.6)" };
}

function popupTierBadge(count) {
    if (count >= 6000) return { label: "★ Flagship region", className: "text-cyan-300" };
    if (count >= 3000) return { label: "Top tier", className: "text-cyan-300" };
    if (count >= 1000) return { label: "Strong secondary", className: "text-cyan-400" };
    if (count >= 500) return { label: "Active region", className: "text-slate-300" };
    return { label: "Growing", className: "text-slate-400" };
}

export default function NorthAmericaMap({ compact = false, showLegend = true }) {
    const [deliveries, setDeliveries] = useState({});
    const [topStates, setTopStates] = useState([]);
    const [hover, setHover] = useState(null);
    const [pinned, setPinned] = useState(null);
    const [position, setPosition] = useState(DEFAULT_POSITION);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailState, setDetailState] = useState(null);

    useEffect(() => {
        api.get("/deliveries-by-region")
            .then((r) => {
                setDeliveries(r.data.deliveries || {});
                setTopStates(r.data.top_states || []);
            })
            .catch(() => {});
    }, []);

    const getCount = (name) => deliveries[name] || 0;

    const legend = useMemo(() => [
        { label: "6,000+", color: "#06b6d4" },
        { label: "3,000+", color: "#0891b2" },
        { label: "2,000+", color: "#0e7490" },
        { label: "1,000+", color: "#155e75" },
        { label: "500+", color: "#164e63" },
        { label: "200+", color: "#0c3a4e" },
        { label: "< 200", color: "#0a2e3d" },
    ], []);

    const zoomIn = () => setPosition((p) => ({ ...p, zoom: Math.min(p.zoom * 1.4, 6) }));
    const zoomOut = () => setPosition((p) => ({ ...p, zoom: Math.max(p.zoom / 1.4, 0.6) }));
    const reset = () => setPosition(DEFAULT_POSITION);

    const renderGeos = (sourceKey) => ({ geographies }) =>
        geographies.map((geo) => {
            const name = geo.properties.name;
            const count = getCount(name);
            const isPinned = pinned?.name === name;
            return (
                <Geography
                    key={`${sourceKey}-${geo.rsmKey}`}
                    geography={geo}
                    onPointerEnter={() => setHover({ name, count })}
                    onPointerLeave={() => setHover(null)}
                    onPointerDown={() => setPinned({ name, count })}
                    onDoubleClick={() => openDetail({ name, count })}
                    style={{
                        default: {
                            fill: isPinned ? "#fb923c" : colorForCount(count),
                            stroke: isPinned ? "#fb923c" : "rgba(15, 118, 144, 0.8)",
                            strokeWidth: isPinned ? 1.4 : 0.6,
                            outline: "none",
                            transition: "fill 0.2s",
                        },
                        hover: {
                            fill: "#f97316",
                            stroke: "#fb923c",
                            strokeWidth: 1.2,
                            outline: "none",
                            cursor: "pointer",
                        },
                        pressed: { outline: "none" },
                    }}
                    data-testid={`map-region-${name.replace(/\s+/g, "-")}`}
                />
            );
        });

    // Click on empty map area clears pinned
    const clearPinned = () => setPinned(null);

    // Opens the right-side detail panel for a given state.
    const openDetail = (s) => {
        if (!s) return;
        setDetailState(s);
        setDetailOpen(true);
    };

    // Active popup: pinned takes precedence over hover
    const active = pinned || hover;

    return (
        <div className="relative" data-testid="north-america-map">
            <div
                className="relative rounded-2xl border border-slate-800 overflow-hidden bg-[#04101a]"
                style={{ aspectRatio: compact ? "4/3" : "16/10" }}
            >
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

                {/* Zoom controls */}
                <div className="absolute top-3 right-3 z-20 flex flex-col gap-1" data-testid="map-zoom-controls">
                    <button
                        onClick={zoomIn}
                        className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-900/85 border border-slate-700 text-slate-100 hover:bg-cyan-500/20 hover:border-cyan-500 hover:text-cyan-300 transition-colors"
                        title="Zoom in"
                        data-testid="map-zoom-in"
                    >
                        <Plus size={16} weight="bold" />
                    </button>
                    <button
                        onClick={zoomOut}
                        className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-900/85 border border-slate-700 text-slate-100 hover:bg-cyan-500/20 hover:border-cyan-500 hover:text-cyan-300 transition-colors"
                        title="Zoom out"
                        data-testid="map-zoom-out"
                    >
                        <Minus size={16} weight="bold" />
                    </button>
                    <button
                        onClick={reset}
                        className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-900/85 border border-slate-700 text-slate-100 hover:bg-orange-500/20 hover:border-orange-500 hover:text-orange-300 transition-colors"
                        title="Reset view"
                        data-testid="map-zoom-reset"
                    >
                        <ArrowCounterClockwise size={15} weight="bold" />
                    </button>
                </div>

                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ scale: compact ? 360 : 520 }}
                    width={1000}
                    height={compact ? 600 : 620}
                    style={{ width: "100%", height: "100%" }}
                >
                    <ZoomableGroup
                        zoom={position.zoom}
                        center={position.coordinates}
                        onMoveEnd={({ coordinates, zoom }) => setPosition({ coordinates, zoom })}
                        minZoom={0.6}
                        maxZoom={6}
                        translateExtent={[[-2000, -1000], [3000, 2500]]}
                    >
                        <Geographies geography={US_STATES_URL}>
                            {renderGeos("us")}
                        </Geographies>
                        <Geographies geography={CANADA_URL}>
                            {renderGeos("ca")}
                        </Geographies>
                    </ZoomableGroup>
                </ComposableMap>

                {/* Hover tooltip - fixed position in map area (always visible when active) */}
                {active && (
                    <div
                        className="absolute z-30 bottom-14 right-3 pointer-events-auto"
                        data-testid="map-popup"
                    >
                        <div
                            className="rounded-xl overflow-hidden border-2 shadow-2xl relative"
                            style={{
                                background: "linear-gradient(135deg, rgba(3,7,18,0.98), rgba(11,17,32,0.98))",
                                ...popupTierStyle(active.count),
                                minWidth: "240px",
                            }}
                        >
                            {pinned && (
                                <button
                                    onClick={clearPinned}
                                    className="absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-slate-800 text-lg leading-none"
                                    data-testid="map-popup-close"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            )}
                            <div className="px-4 py-3 border-b border-slate-800/80">
                                <div className="flex items-center gap-2">
                                    <span
                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                        style={{ background: colorForCount(active.count), boxShadow: `0 0 10px ${colorForCount(active.count)}` }}
                                    />
                                    <div className="font-heading font-bold text-slate-50 text-base leading-tight">{active.name}</div>
                                </div>
                                <div className="overline mt-1 !text-slate-500">Region · Surveys2CAD</div>
                            </div>
                            <div className="px-4 py-3">
                                {active.count > 0 ? (
                                    <>
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-heading font-extrabold text-3xl font-mono-alt" style={{ color: colorForCount(active.count) }}>
                                                {formatCount(active.count)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-400 mt-1 font-medium">drawings delivered</div>
                                        <div className="mt-2 flex items-center gap-2 text-[10px] font-mono-alt uppercase tracking-wider">
                                            {(() => {
                                                const tier = popupTierBadge(active.count);
                                                return <span className={tier.className}>{tier.label}</span>;
                                            })()}
                                        </div>
                                        <button
                                            onClick={() => openDetail(active)}
                                            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-heading font-semibold text-cyan-300 hover:text-cyan-200 border border-cyan-500/40 hover:border-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-lg py-1.5 transition-colors"
                                            data-testid="map-popup-detail-btn"
                                        >
                                            View region details <ArrowRight size={12} weight="bold" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="font-heading font-bold text-xl text-slate-300">Available on request</div>
                                        <div className="text-xs text-slate-500 mt-1">Coverage on demand — contact for details</div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Top states overlay badge */}
                {!compact && topStates.length > 0 && (
                    <div className="absolute top-3 left-3 tech-card p-3 max-w-[220px]" data-testid="top-states-badge">
                        <div className="overline mb-2">Top 6 regions</div>
                        <ul className="space-y-0.5">
                            {topStates.map((s) => (
                                <li key={s.name}>
                                    <button
                                        onClick={() => openDetail({ name: s.name, count: s.count })}
                                        className="w-full flex items-center justify-between gap-3 text-xs px-2 py-1 rounded hover:bg-cyan-500/10 transition-colors group"
                                        data-testid={`top-state-${s.name.replace(/\s+/g, "-")}`}
                                    >
                                        <span className="text-slate-200 group-hover:text-cyan-200">{s.name}</span>
                                        <span className="font-mono-alt text-cyan-300 font-semibold">{formatCount(s.count)}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Zoom indicator */}
                <div className="absolute bottom-3 right-3 font-mono-alt text-[10px] text-slate-400 bg-slate-900/70 border border-slate-800 rounded-md px-2 py-1" data-testid="zoom-indicator">
                    {position.zoom.toFixed(1)}×
                </div>

                {/* Drag hint */}
                <div className="absolute bottom-3 left-3 font-mono-alt text-[10px] text-slate-500 bg-slate-900/70 border border-slate-800 rounded-md px-2 py-1">
                    drag to pan · scroll / buttons to zoom
                </div>
            </div>

            {showLegend && (
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                    <span className="overline">Drawings delivered</span>
                    {legend.map((l) => (
                        <span key={l.label} className="flex items-center gap-2">
                            <span className="w-3.5 h-3.5 rounded-sm" style={{ background: l.color }} />
                            <span className="font-mono-alt">{l.label}</span>
                        </span>
                    ))}
                </div>
            )}

            {/* Click-to-open detail panel */}
            <StateDetailPanel
                open={detailOpen}
                state={detailState}
                onClose={() => setDetailOpen(false)}
            />
        </div>
    );
}
